import {useEffect, useMemo, useRef, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import SelectField from "../../components/SelectField";
import InputField from "../../components/InputField";
import useTranslation from "../../hooks/translations";
import {useApi} from "../../contexts/ApiProvider";
import CostTypeService from "../../services/CostTypeService";
import CostCategoryService from "../../services/CostCategoryService";
import CurrencyService from "../../services/CurrencyService";
import DatePickerField from "../../components/DatePickerField";
import CostService from "../../services/CostService";
import DatesUtils from "../../utils/dates";

export default function CostModal({
                                    showModal,
                                    title,
                                    data,
                                    onModalSubmit,
                                    onModalClose
                                  }) {
  const translations = useTranslation();

  const api = useApi();
  const currencyService = useMemo(() => new CurrencyService(api), [api]);
  const costTypeService = useMemo(() => new CostTypeService(api), [api]);
  const costCategoryService = useMemo(() => new CostCategoryService(api), [api]);
  const costService = useMemo(() => new CostService(api), [api]);

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedCurrencyOption, setSelectedCurrencyOption] = useState("");
  const handleCurrencyOptionChange = (option) => {
    setSelectedCurrencyOption(option.currentTarget.value);
  };

  const [costTypeOptions, setCostTypeOptions] = useState([]);
  const [selectedCostTypeOption, setSelectedCostTypeOption] = useState("");
  const handleCostTypeOptionChange = (option) => {
    setSelectedCostTypeOption(option.currentTarget.value);
  };

  const [costCategoryOptions, setCostCategoryOptions] = useState([]);
  const [filteredCostCategoryOptions, setFilteredCostCategoryOptions] = useState([]);
  const [selectedCostCategoryOption, setSelectedCostCategoryOption] = useState("");
  const handleCostCategoryOptionChange = (option) => {
    setSelectedCostCategoryOption(option.currentTarget.value);
  };

  const nameField = useRef();
  const descriptionField = useRef();
  const dateField = useRef();
  const typeField = useRef();
  const categoryField = useRef();
  const amountField = useRef();
  const currencyField = useRef();
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const getCurrencies = async () => {
      const response = await currencyService.getAll();
      const currencyOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setCurrencyOptions(currencyOptions);
    }
    (async () => {
      await getCurrencies();
    })();
  }, [currencyService]);

  useEffect(() => {
    const getCostTypes = async () => {
      const response = await costTypeService.getAll();
      const typeOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setCostTypeOptions(typeOptions);
    }
    (async () => {
      await getCostTypes();
    })();
  }, [costTypeService]);

  useEffect(() => {
    const getCostCategories = async () => {
      const response = await costCategoryService.getAll();
      const categoryOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name,
          type: item.type.id
        };
      });
      setCostCategoryOptions(categoryOptions);
      setFilteredCostCategoryOptions(categoryOptions);
    }
    (async () => {
      await getCostCategories();
    })();
  }, [costCategoryService]);

  useEffect(() => {
    if (selectedCostCategoryOption) {
      let typeToSelect = costCategoryOptions.filter((option) => option.value === Number(selectedCostCategoryOption))[0]
      if (typeToSelect && typeToSelect.type !== selectedCostTypeOption) {
        setSelectedCostCategoryOption("")
      }
    }

    if (!selectedCostTypeOption) {
      setFilteredCostCategoryOptions(costCategoryOptions);
      return;
    }

    setFilteredCostCategoryOptions(costCategoryOptions.filter(
      (option) => option.type === Number(selectedCostTypeOption))
    );
  }, [selectedCostTypeOption]);

  useEffect(() => {
    let typeToSelect = costCategoryOptions.filter((option) => option.value === Number(selectedCostCategoryOption))[0]
    if (typeToSelect) {
      setSelectedCostTypeOption(typeToSelect.type)
    }
  }, [selectedCostCategoryOption]);

  useEffect(() => {
    (async () => {
      if (Object.keys(data).length > 0) {
        nameField.current.value = data.name;
        descriptionField.current.value = data.description;
        dateField.current.value = data.date;
        amountField.current.value = data.amount;
        setSelectedCurrencyOption(data.currencyId);
        setSelectedCostTypeOption(data.typeId);
        setSelectedCostCategoryOption(data.categoryId);
      } else {
        amountField.current.focus();
        dateField.current.value = DatesUtils.formatDate(new Date());
        if (currencyOptions.length > 0) {
          setSelectedCurrencyOption(currencyOptions[0].value);
        }
      }
    })();
  }, [data, currencyOptions]);

  const onSubmit = async (event) => {
    event.preventDefault();

    const name = nameField.current.value;
    const description = descriptionField.current.value;
    const date = dateField.current.value;
    const type = parseInt(typeField.current.value);
    const category = parseInt(categoryField.current.value);
    const amount = parseFloat(amountField.current.value);
    const currency = parseInt(currencyField.current.value);

    const errors = {};
    if (!name) {
      errors.name = translations.errorName;
    }
    if (!date) {
      errors.date = translations.errorDate;
    }
    if (!type) {
      errors.type = translations.errorType;
    }
    if (!category) {
      errors.category = translations.errorCategory;
    }
    if (!amount) {
      errors.amount = translations.errorAmount;
    }
    if (!currency) {
      errors.currency = translations.errorCurrency;
    }
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const dateObject = new Date(date);

    const submitData = {
      name: name,
      description: description,
      timestamp: DatesUtils.formatDate(dateObject),
      type_id: type,
      category_id: category,
      amount: amount,
      currency_id: currency,
    }

    if (data.id) {
      const response = await costService.patch(data.id, submitData)
      if (!response.ok) {
        return
      }
    } else {
      const response = await costService.create(submitData)
      if (!response.ok) {
        return
      }
    }

    onModalSubmit(submitData)
  };

  return (
    <Modal show={showModal} onHide={onModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title ? title : translations.cost}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit} id="costForm">
          <div className="mb-3">
            <DatePickerField
              name="inputDate"
              label={translations.date}
              placeholder={translations.enterDate}
              error={formErrors.date}
              fieldRef={dateField}
            />
          </div>
          <div className="mb-3">
            <InputField
              name="inputAmount"
              type="number"
              step=".01"
              inputmode="decimal"
              label={translations.amount}
              placeholder={translations.enterAmount}
              error={formErrors.amount}
              fieldRef={amountField}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="currency"
              label={translations.currency}
              placeholder={translations.enterCurrency}
              options={currencyOptions}
              selectedOption={selectedCurrencyOption}
              handleChange={handleCurrencyOptionChange}
              error={formErrors.currency}
              fieldRef={currencyField}
            />
          </div>
          <div className="mb-3">
            <InputField
              name="inputName"
              label={translations.name}
              placeholder={translations.enterName}
              error={formErrors.name}
              fieldRef={nameField}
            />
          </div>
          <div className="mb-3">
            <InputField
              name="inputDescription"
              label={translations.description}
              placeholder={translations.enterDescription}
              error={formErrors.description}
              fieldRef={descriptionField}
              asTextarea={true}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="costType"
              label={translations.type}
              placeholder={translations.enterType}
              options={costTypeOptions}
              selectedOption={selectedCostTypeOption}
              handleChange={handleCostTypeOptionChange}
              error={formErrors.type}
              fieldRef={typeField}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="costCategory"
              label={translations.category}
              placeholder={translations.enterCategory}
              options={filteredCostCategoryOptions}
              selectedOption={selectedCostCategoryOption}
              handleChange={handleCostCategoryOptionChange}
              error={formErrors.category}
              fieldRef={categoryField}
            />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" type="submit"
                form="costForm">{translations.save}</Button>
        <Button variant="secondary"
                onClick={onModalClose}>{translations.close}</Button>
      </Modal.Footer>
    </Modal>
  )
}
